import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import {
  makeStyles,
  Typography,
  IconButton,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import studioApi from '../../services/studio-api';
import Listing from './filePickerListing';

const useStyles = makeStyles((theme) => ({
  image: {
    width: '100px',
    height: '56px',
    objectFit: 'contain'
  },
  imagePlaceholder: {
    border: '1px solid #ccc',
    height: '56px',
    width: '100px',
  },
  flexRight: {
    marginLeft: 'auto'
  },
  url: {
    color: 'rgba(0, 0, 0, 0.38)',
    fontSize: '11px'
  },
  dialog: {
    height: '60%',
    width: '60%',
    maxWidth: 'none',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      width: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: 0
    },
  }
}));

const dialogTitleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(dialogTitleStyles)((props) => {
  const { children, classes, onClose, ...rest } = props;
  const { t } = useTranslation();

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...rest}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label={t('common:close')} title={t('common:close')} className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const FilePickerDialog = (props) => {
  const [ selectedFiles, setSelectedFiles ] = useState(props.selectedFiles || []);
  const [ loading, setLoading ] = useState(false);
  const [ items, setItems ] = useState(null);
  const { t } = useTranslation();
  const classes = useStyles();

  const rootFolderId = props.rootFolderId || '';

  const openFolder = (folderId, folderName) => {
    setLoading(true);

    studioApi.getFolder(folderId).then(res => {
      setItems(res);
      setLoading(false);
    });
  };

  const handleBreadCrumbClick = (folderId, position) => {
    openFolder(folderId);
  }

  const handleFolderClick = (folderId, folderName) => {
    openFolder(folderId);
  }

  const handleFileClick = (clickedFile) => {
    if(selectedFiles.some(file => file.id === clickedFile.id)) {
      // Unselect
      setSelectedFiles(selectedFiles.filter(file => file.id !== clickedFile.id));
    } else {
      if(props.multiselect) {
        setSelectedFiles([
          ...selectedFiles,
          clickedFile
        ])
      } else {
        setSelectedFiles([
          clickedFile
        ]);
      }
    }
  }

  const handleSelect = () => {
    if(props.multiselect) {
      props.handleSelect(selectedFiles);
    } else {
      props.handleSelect(selectedFiles[0]);
    }
  }

  useEffect(() => {
    if(props.open) {
      openFolder(rootFolderId);
    }
  }, [props.open]);


  return (
    <Dialog onClose={props.handleClose} aria-labelledby="dialog-title" open={props.open} classes={{ paper: classes.dialog}}>
      <DialogTitle id="dialog-title" onClose={props.handleClose}>
        {t(`filePicker:${props.multiselect ? 'selectFilesForField' : 'selectFileForField'}`)}: {props.title}
      </DialogTitle>
      <DialogContent dividers>
        <Listing loading={loading} items={items} handleFolderClick={handleFolderClick} selectedFiles={selectedFiles} handleFileClick={handleFileClick} handleBreadCrumbClick={handleBreadCrumbClick} />
      </DialogContent>
      <DialogActions>
        <Button disabled={!selectedFiles.length} onClick={handleSelect} color="primary">
          {t(`filePicker:${props.multiselect ? 'selectFiles' : 'selectFile'}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FilePickerDialog.propTypes = {
  rootFolderId: PropTypes.string,
  multiselect: PropTypes.bool,
  open: PropTypes.bool,
  title: PropTypes.string,
  handleSelect: PropTypes.func,
  handleClose: PropTypes.func,
  selectedFiles: PropTypes.array,
}

export default FilePickerDialog;
